import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { Input } from "@/components/ui/baseUi/input"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import type { ScoreType } from "@/modules/scores/scoreTypes"
import { getScoreTypeLabel } from "@/modules/scores/scoreUtils"
import type { ExerciseFormViewEnum } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseForm"
import type {
  ExerciseFormikType,
  ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { createExerciseScoreFormModel } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/models/exerciseScoreFormModel"
import type { SimpleFormik } from "@/typings/formik"
import { InfoIconDark, InfoIconLight } from "@images/svgIcons/infoIcon"
import { word } from "@modules/core/utils/i18n"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { PencilIcon, PlusIcon, TrashIcon } from "lucide-react"

export function ExerciseScoreInput({
  formik,
  setCurrentView,
  className,
  ...props
}: {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  setCurrentView: (view: ExerciseFormViewEnum) => void
} & React.ComponentProps<"div">) {
  const confirmPopup = useConfirmPopup()
  const { theme } = useTheme()

  const model = createExerciseScoreFormModel(formik.values.scoreSetConfigurations, formik.values.scoringOption)

  const removeScoreSetConfigurations = async () => {
    const confirmationModaleContent = (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col items-center justify-center gap-2.5">
          {theme === Theme.OwnerDark ? (
            <InfoIconDark color="currentColor" className="text-red-500" />
          ) : (
            <InfoIconLight color="currentColor" className="text-red-500" />
          )}
          <Text fontStyle="TL" className="text-center text-textDefault-body">
            {word("program.planning.create.exercise.askScore.delete.confirmation.title")}
          </Text>
        </div>

        <Text fontStyle="BM" className="text-center whitespace-pre-line text-textDefault-body">
          {word("program.planning.create.exercise.askScore.delete.confirmation.message")}
        </Text>
      </div>
    )

    const handleDeleteScoreSetConfigurations = async () => {
      await formik.setFieldValue("scoreSetConfigurations", [])
      await formik.setFieldValue("scoringOption", null)
      await formik.setFieldValue("requiredMedia", false)
    }

    if (formik.values.scoreSetConfigurations.some((scoreSet) => !!scoreSet.id)) {
      confirmPopup.show({
        title: "",
        message: confirmationModaleContent,
        accept: async () => {
          await handleDeleteScoreSetConfigurations()
        },
        classNames: {
          dialogContent: "w-128",
        },
        footerProps: {
          cancelText: word("global.cancel"),
          confirmText: word("global.continue"),
        },
      })
    } else {
      await handleDeleteScoreSetConfigurations()
    }
  }

  const handleNavigateToScoreForm = () => {
    setCurrentView("scoreContent")
  }

  return (
    <div className={cn("w-full rounded-2xl bg-surface-foreground px-5 py-6", className)} {...props}>
      {!formik.values.scoreSetConfigurations || formik.values.scoreSetConfigurations.length === 0 ? (
        <div className="flex flex-row items-center justify-start gap-3">
          <div
            onClick={handleNavigateToScoreForm}
            className="transition-duration-300 flex cursor-pointer items-center justify-center rounded-full bg-neutral-200 transition-all hover:bg-neutral-300"
            style={{ height: 40, width: 40 }}
          >
            <PlusIcon color="currentColor" className="text-textDefault-body" size={24} strokeWidth={1} />
          </div>
          <div className="flex flex-col items-start gap-1">
            <Text className="font-bold" fontStyle="LL">
              {word("program.planning.create.exercise.askScore.titleBloc")}
            </Text>
            <Text className="text-textDefault-caption" fontStyle="BS">
              {word("program.planning.create.exercise.askScore.descriptionBloc")}
            </Text>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Text fontStyle="LS" className="text-textDefault-body uppercase">
            {word("sessionForm.exercise.scoreForm.scoreboardSumUp.label")}
          </Text>

          <div className="flex flex-col gap-4">
            {formik.values.scoreSetConfigurations.map((scoreSet, index) => (
              <ScoreSetRowSumUp key={index} index={index} scoreType={scoreSet.scoreType} />
            ))}
          </div>

          {model.scoreResultText && (
            <Text fontStyle="BS" className="text-center text-textDefault-body">
              {model.scoreResultText}
            </Text>
          )}

          <div className="flex flex-row items-center justify-end gap-2.5">
            <Button variant="secondarySecondary" size="iconMd" onClick={handleNavigateToScoreForm}>
              <PencilIcon color="currentColor" className="text-textDefault-body" />
            </Button>
            <Button variant="secondarySecondary" size="iconMd" onClick={() => void removeScoreSetConfigurations()}>
              <TrashIcon color="currentColor" className="text-textDefault-body" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function ScoreSetRowSumUp({
  index,
  scoreType,
  className,
  ...props
}: {
  index: number
  scoreType: ScoreType
} & React.ComponentProps<"div">) {
  const scoreTypeLabel = getScoreTypeLabel(scoreType)
  return (
    <div className={cn("flex flex-row items-center justify-between gap-4", className)} {...props}>
      <Text fontStyle="TM" className="text-textDefault-body">
        {index + 1}.
      </Text>

      <Input value={scoreTypeLabel} className="w-full bg-surface-background disabled:opacity-100" readOnly disabled />
    </div>
  )
}
